import { FCC } from "@dgs/core";
import { createContext, useCallback, useContext } from "react";
import { useContactViewsQuery } from "../../shared/api/contactViewService";
import { useUserPreferencesQuery } from "../../shared/api/userPreferencesQueries";
import { useStateWithDependency } from "../../shared/hooks";
import { ContactViewIndexResource } from "../../shared/types/contactView";

interface GlobalContactProviderState {
	globalContactView: ContactViewIndexResource | null;
	contactViews: ContactViewIndexResource[];
	favoriteContactViews: ContactViewIndexResource[];
	setGlobalContactView: (contactView: ContactViewIndexResource) => void;
	isLoading: boolean;
}

const CONTACT_VIEW_KEY = "contactViewId";

const GlobalContactViewContext = createContext<GlobalContactProviderState | undefined>(undefined);

const getCurrentContactView = (contactViews: ContactViewIndexResource[]) => {
	const id = localStorage.getItem(CONTACT_VIEW_KEY);

	if (!contactViews.length) {
		return null;
	}

	if (!id) {
		return contactViews[0];
	}

	const parsedId = Number(id);
	return contactViews.find((contactView) => contactView.id === parsedId) || contactViews[0];
};

export const GlobalContactViewProvider: FCC = ({ children }) => {
	const { data: contactViews, isLoading } = useContactViewsQuery();
	const { userPreferences } = useUserPreferencesQuery();
	const [globalContactView, _setContactView] = useStateWithDependency<ContactViewIndexResource | null>(
		getCurrentContactView(contactViews),
	);
	const favoriteContactViewIds = userPreferences ? userPreferences.contactViews.map((v) => v.id) : [];

	const setGlobalContactView = useCallback(
		(contactView: ContactViewIndexResource) => {
			localStorage.setItem(CONTACT_VIEW_KEY, `${contactView.id}`);
			_setContactView(contactView);
		},
		[_setContactView],
	);

	return (
		<GlobalContactViewContext.Provider
			value={{
				globalContactView,
				favoriteContactViews: contactViews.filter((view) => favoriteContactViewIds.includes(view.id)),
				contactViews: contactViews.filter((view) => !favoriteContactViewIds.includes(view.id)),
				setGlobalContactView,
				isLoading,
			}}
		>
			{children}
		</GlobalContactViewContext.Provider>
	);
};

export const useGlobalContactView = () => {
	const ctx = useContext(GlobalContactViewContext);

	if (ctx === undefined) {
		throw new Error("useGlobalContactView should be wrapped in a GlobalContactViewProvider");
	}

	return ctx;
};
